import { yupResolver } from "@hookform/resolvers/yup";
import { Key } from "@phosphor-icons/react";
import { motion } from "motion/react";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import brandLogo from "../../../../assets/images/Logo.png";
import Loading from "../../../../components/Loading";
import { ILoginResponse, UserRole } from "../../../../interfaces";
import { httpService } from "../../../../services/httpService";
import { API_CONFIG } from "../../../../shared/constants";
import { useAuth } from "../../../../hooks";
import {
  MeshBackground,
  useGlobalStyles,
  Field,
  CtaButton,
  IcoUser,
  IcoLock,
  IcoArrow,
} from "../../../AuthShared";
import ThemeToggle from "../../../../components/ThemeToggle";

interface FormData {
  name: string;
  password: string;
  confirmPassword: string;
}

interface InviteData {
  uidb64: string;
  token: string;
}

interface IUserDetails {
  is_exist: boolean;
  is_active: boolean;
  is_invited: boolean;
  is_owner: boolean;
  invited_status: "active" | "pending" | "rejected";
}

const EASE = [0.22, 1, 0.36, 1];

/* ── Page shell ─────────────────────────────────────────────────────────── */
function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col text-gray-900 dark:text-gray-100 dark:bg-neutral-950 [font-family:var(--font-geist)]">
      <MeshBackground />

      <div className="absolute top-4 right-4 z-[10]">
        <ThemeToggle />
      </div>

      <div className="relative flex-1 flex items-center justify-center px-4 z-[2]">
        <div className="w-full" style={{ maxWidth: "clamp(360px, 90vw, 460px)" }}>

          {/* Logo */}
          <motion.div
            className="flex justify-center mb-5"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <img src={brandLogo} alt="AppRevBooster" className="h-12 object-contain" />
          </motion.div>

          {/* Glass card */}
          <motion.div
            className="relative overflow-hidden p-[24px_24px_22px] sm:p-[28px_28px_26px] bg-white/[0.88] dark:bg-neutral-900/95 backdrop-blur-[28px] saturate-[160%] border border-black/[0.09] dark:border-neutral-700 rounded-2xl shadow-xl"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.08, ease: EASE }}
          >
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{
                background: "linear-gradient(90deg, transparent, #1a3d7c, transparent)",
                backgroundSize: "200% 100%",
                animation: "arb-glow-trail 4s linear infinite",
              }}
            />
            {children}
          </motion.div>
        </div>
      </div>

      {/* Bottom status bar */}
      <motion.div
        className="relative flex-shrink-0 z-[4] flex flex-wrap justify-center gap-x-3 gap-y-1 py-3 text-[11px] sm:text-[11.5px] text-gray-400 [font-family:var(--font-mono)] tracking-[0.3px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.5, ease: EASE }}
      >
        <a href="https://apprevbooster.com/privacy-policy" target="_blank" rel="noreferrer" className="text-inherit no-underline hover:text-gray-600 transition-colors">Privacy</a>
        <span>·</span>
        <a href="https://apprevbooster.com/terms-of-service" target="_blank" rel="noreferrer" className="text-inherit no-underline hover:text-gray-600 transition-colors">Terms</a>
      </motion.div>
    </div>
  );
}

const AcceptInvite: React.FC = () => {
  useGlobalStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { permittedRoutes, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userDetails, setUserDetails] = useState<IUserDetails | null>(null);

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t("configurations.team.acceptInvite.nameRequired")),
    password: yup
      .string()
      .required(t("configurations.team.acceptInvite.passwordRequired"))
      .min(8, t("configurations.team.acceptInvite.passwordMinLength")),
    confirmPassword: yup
      .string()
      .required(t("configurations.team.acceptInvite.confirmPasswordRequired"))
      .oneOf(
        [yup.ref("password")],
        t("configurations.team.acceptInvite.passwordsDoNotMatch")
      ),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver<FormData>(schema),
    mode: "onChange",
    defaultValues: { name: "", password: "", confirmPassword: "" },
  });

  useEffect(() => {
    const uidb64 = searchParams.get("uidb64");
    const token = searchParams.get("token");
    const rawEmail = searchParams.get("email");
    const email = rawEmail ? decodeURIComponent(rawEmail) : null;

    if (!uidb64 || !token || !email) {
      setError(t("auth.invalidInvite"));
      setIsLoading(false);
      return;
    }

    try {
      setInviteData({ uidb64, token });
      httpService
        .get<IUserDetails>(
          API_CONFIG.path.userDetailsFromEmail,
          { email },
          { headers: { Authorization: undefined } }
        )
        .then((response) => {
          setUserDetails(response);
          setIsLoading(false);
        })
        .catch((err: unknown) => {
          console.error("Error fetching user details:", err);
          setIsLoading(false);
        });
    } catch (err: unknown) {
      console.error("Error decoding invite data:", err);
      setError(t("auth.invalidInvite"));
      setIsLoading(false);
    }
  }, [searchParams, t]);

  const onSubmit = async (data: FormData) => {
    try {
      const uidb64 = inviteData?.uidb64;
      const token = inviteData?.token;

      if (!uidb64 || !token) {
        setError(t("auth.invalidInvite"));
        return;
      }
      setIsSubmitting(true);
      httpService
        .post<ILoginResponse>(
          `${API_CONFIG.path.acceptInvite}/${uidb64}/${token}`,
          {
            name: data.name,
            password: data.password,
            confirmPassword: data.confirmPassword,
          }
        )
        .then((response) => {
          const { user, access, refresh } = response;
          const userData = { ...user, role: "sub_admin" as UserRole };
          httpService.setTokens({ access, refresh });
          httpService.setUserData(userData);
          setUser(userData);
          setIsSubmitting(false);

          if (userDetails?.is_owner) {
            window.location.href = "/integrations?trackingId=add";
          } else {
            window.location.href =
              (permittedRoutes[0] || {})?.path || "/dashboard";
          }
        })
        .catch((err: unknown) => {
          setIsSubmitting(false);
          console.error("error:", err);
          toast.error(err instanceof Error ? err.message : t("auth.acceptInviteError"));
        });
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t("auth.acceptInviteError"));
      }
    }
  };

  /* ── Loading state ───────────────────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className="fixed inset-0 dark:bg-neutral-950 bg-[#f4f6fb]">
        <Loading />
      </div>
    );
  }

  /* ── Error / invalid invite state ───────────────────────────────────── */
  if (error || !inviteData) {
    return (
      <PageShell>
        <motion.div
          className="flex flex-col items-center text-center gap-4 py-2"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15, ease: EASE }}
        >
          <motion.div
            className="w-14 h-14 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.22, ease: EASE }}
          >
            <Key size={32} weight="duotone" className="text-amber-500" />
          </motion.div>
          <motion.div
            className="flex flex-col gap-1"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.3, ease: EASE }}
          >
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
              {t("configurations.team.acceptInvite.invalidInvite")}
            </h2>
            <p className="text-[13px] text-gray-500 dark:text-neutral-400 leading-relaxed">
              {error || t("configurations.team.acceptInvite.inviteExpiredOrInvalid")}
            </p>
          </motion.div>
          <motion.div
            className="w-full"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.38, ease: EASE }}
          >
            <button
              type="button"
              onClick={() => navigate("/")}
              className="mt-1 w-full py-3.5 px-5 rounded-[10px] text-[14px] font-semibold flex items-center justify-center gap-2 transition-all duration-200 min-h-[49px] bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-[0_2px_14px_rgba(26,61,124,0.28)] hover:shadow-[0_4px_20px_rgba(26,61,124,0.38)] hover:-translate-y-px active:translate-y-0 cursor-pointer border-none [font-family:inherit]"
            >
              {t("auth.backToLogin")}
              <IcoArrow size={16} />
            </button>
          </motion.div>
        </motion.div>
      </PageShell>
    );
  }

  /* ── Accept invite form ──────────────────────────────────────────────── */
  return (
    <PageShell>
      <div className="flex flex-col gap-5">
        <motion.div
          className="flex flex-col gap-1"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: EASE }}
        >
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            {t("configurations.team.acceptInvite.title")}
          </h2>
          <p className="text-[13px] text-gray-500 dark:text-neutral-400 leading-relaxed">
            {t("configurations.team.acceptInvite.welcomeMessage")}
          </p>
          {inviteData && (
            <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/40">
              <IcoUser size={13} className="text-indigo-400 shrink-0" />
              <span className="text-[12px] text-indigo-700 dark:text-indigo-300 font-medium truncate">
                {searchParams.get("email") ? decodeURIComponent(searchParams.get("email")!) : ""}
              </span>
            </div>
          )}
        </motion.div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.22, ease: EASE }}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Field
                  label={t("configurations.team.acceptInvite.name")}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  autoComplete="name"
                  error={errors.name?.message}
                  leading={<IcoUser size={15} />}
                />
              )}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.29, ease: EASE }}
          >
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <Field
                  label={t("configurations.team.acceptInvite.password")}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  type="password"
                  autoComplete="new-password"
                  error={errors.password?.message}
                  leading={<IcoLock size={15} />}
                />
              )}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.36, ease: EASE }}
          >
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <Field
                  label={t("configurations.team.acceptInvite.confirmPassword")}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  type="password"
                  autoComplete="new-password"
                  error={errors.confirmPassword?.message}
                  leading={<IcoLock size={15} />}
                />
              )}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.43, ease: EASE }}
          >
            <CtaButton disabled={isSubmitting} loading={isSubmitting}>
              <span>
                {isSubmitting
                  ? t("configurations.team.acceptInvite.submitting")
                  : t("configurations.team.acceptInvite.submit")}
              </span>
              {!isSubmitting && <IcoArrow size={16} />}
            </CtaButton>
          </motion.div>
        </form>
      </div>
    </PageShell>
  );
};

export default AcceptInvite;
